import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { GoDialogRef } from '@go-modules/services/go-dialog-ref/go-dialog-ref';
import { GO_MODAL_DATA } from '@go-modules/services/go-modal/go-modal.tokens';
import { LibraryCollectionFolder, LibraryService } from '@go-modules/services/library/library.service';
import { InvalidValueValidator } from '@go-modules/validators/invalid-value';
import { NgxFeatureFlagService } from '@go-modules/services/feature-flag/feature-flag.service';

@Component({
	selector: 'ngx-manage-library-collection-folder',
	templateUrl: './manage-library-collection-folder.component.html',
	styleUrls: ['./manage-library-collection-folder.component.scss'],
	standalone: false
})
export class ManageLibraryCollectionFolderComponent implements OnInit {
	public form = new UntypedFormGroup({
		name: new UntypedFormControl('', [Validators.required]),
		selectedFolder: new UntypedFormControl(null, []),
		selectedCollection: new UntypedFormControl(null, [])
	});

	public useTreeView = this.featureFlagService.isAvailable('LIB_FOLDERS');
	public folders: LibraryCollectionFolder[] = [];
	public initialFolderToSelect: LibraryCollectionFolder | null = null;

	constructor (
		public dialogRef: GoDialogRef,
		@Inject(GO_MODAL_DATA) public data: {
			collections: any[];
			existingFolders: LibraryCollectionFolder[];
			createMode: boolean;
			selectedCollection: any;
			folder: LibraryCollectionFolder;
		},
		private libraryService: LibraryService,
		private featureFlagService: NgxFeatureFlagService
	) {}

	public ngOnInit () {
		if (this.data.createMode) {
			if (!this.data.selectedCollection) {
				return this.dialogRef.close({request_failed: true});
			}
			this.form.controls.selectedCollection.setValue(this.data.selectedCollection);

			if (this.useTreeView) {
				if (this.data.folder) {
					this.initialFolderToSelect = this.data.folder;
				}
				this.loadFoldersForTree(this.form.value.selectedCollection.id);
			} else {
				this.updateNameValidation();
			}
		} else {
			if (!this.data.folder || !this.data.folder.id) {
				return this.dialogRef.close({ request_failed: true });
			}
			this.form.controls.name.setValue(this.data.folder.name);

			if (this.useTreeView) {
				this.loadFoldersForTree(this.data.folder.library_collection_id);
			} else {
				this.updateNameValidation();
			}
		}
	}

	private loadFoldersForTree (collectionId: number) {
		this.libraryService.getCollectionFolders(collectionId)
			.subscribe((folders: LibraryCollectionFolder[]) => {
				this.folders = folders;
				this.updateNameValidation();
			});
	}

	public loadFolders () {
		this.libraryService.getCollectionFolders(this.form.value.selectedCollection.id)
			.subscribe((folders: LibraryCollectionFolder[]) => {
				this.data.existingFolders = folders;
				this.updateNameValidation();
			});
	}

	private updateNameValidation () {
		// re-validate folderName to check unique name
		this.form.controls.name.clearValidators();
		const existingFolders = this.useTreeView ? this.folders : this.data.existingFolders;
		let existingNames = existingFolders.map((folder) => folder.name.toLowerCase().trim());
		if (!this.data.createMode) {
			existingNames = existingNames.filter((name) => name !== this.data.folder.name.toLowerCase().trim());
		}
		this.form.controls.name.addValidators([Validators.required, InvalidValueValidator(existingNames)]);
		this.form.controls.name.updateValueAndValidity();
	}

	public dismiss () {
		this.dialogRef.close({ dismissed: true });
	}

	public submit (form) {
		if (form.invalid) {
			return;
		}

		if (form.pristine) {
			return this.dismiss();
		}

		if (this.data.createMode) {
			// if selectedFolder is null, it's root level
			const selectedFolder = this.form.value.selectedFolder;
			const parentId = selectedFolder ? selectedFolder.id : null;

			this.libraryService.createFolder(
				this.form.value.selectedCollection.id,
				this.form.value.name,
				parentId
			)
				.subscribe({
					next: (newFolder) => {
						this.dialogRef.close(newFolder);
					},
					error: (error) => {
						this.dialogRef.close({ request_failed: true, error });
					}
				});
		} else {
			this.libraryService.editFolder(this.data.folder.id, this.form.value.name)
				.subscribe({
					next: (updatedFolder) => {
						this.dialogRef.close(updatedFolder);
					},
					error: (error) => {
						this.dialogRef.close({ request_failed: true, error });
					}
				});
		}
	}

	public onAvailableFoldersChange (availableFolders: LibraryCollectionFolder[]) {
		if (this.initialFolderToSelect) {
			const isAvailable = availableFolders.some((f) => f.id === this.initialFolderToSelect.id);
			this.form.controls.selectedFolder.setValue(isAvailable ? this.initialFolderToSelect : null);
			this.initialFolderToSelect = null;
		}
	}

	public getTitleTranslationKey () {
		return this.data.createMode ? 'add-collection-folder_title' : 'edit-collection-folder_label';
	}

	public getPlaceholderKey () {
		return this.data.createMode ? 'add-collection-folder_placeholder' : 'edit-collection-folder_placeholder';
	}

	public getButtonName () {
		return this.data.createMode ? 'common_add' : 'common_rename';
	}
}
